import React, { useState, useMemo, useRef } from 'react';
import { Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import {
    useCreateProjectMutation,
    useGetRegionsQuery,
    useGetFacilitiesQuery,
} from '../../store';
import { Modal } from '../../components';
import { QuickAlert, SelectField } from '../../components';
import { getDefaultFacilityByLocation } from '../../components/projectLocationUtils';
import { useDispatch } from 'react-redux';

export const NewProjectModal = (props) => {
    const { show, onClose } = props;

    const navigate = useNavigate();

    const dispatch = useDispatch();

    const formikRef = useRef(null);

    const [createProject] = useCreateProjectMutation();

    const { data: regions = [] } = useGetRegionsQuery();

    const { data: facilities = [] } = useGetFacilitiesQuery();

    const handleSubmit = async (values, formikHelpers) => {
        const res = await createProject(values).unwrap();
        formikHelpers.setSubmitting(false);
        onClose();
        navigate(`/projects/${res.id}`);
    };

    const handleValidate = (values) => {
        let errors = {};
        if (!(values.name?.length > 0)) {
            errors['name'] = 'Name cannot be blank';
        }
        return errors;
    };

    const uniqueCountries = useMemo(() => {
        return [
            ...new Set(regions.filter((r) => r.country).map((r) => r.country)),
        ].map((c) => ({ name: c, value: c }));
    }, [regions]);

    const initialCountry = uniqueCountries[0]?.value || '';

    const uniqueStates = useMemo(() => {
        return [
            ...new Set(
                regions
                    .filter((r) => r.country === initialCountry)
                    .map((r) => r.stateName),
            ),
        ].map((s) => ({ name: s, value: s }));
    }, [regions, initialCountry]);

    const initialState = uniqueStates[0]?.value || '';

    const facilityList = useMemo(() => {
        return [
            ...new Set(facilities.filter((f) => f.label === 'Blue Label')),
        ].map((c) => ({ name: c.facility, value: c.facility }));
    }, [facilities]);

    const initialFacility = facilityList[0]?.value || '';

    const [selectedCountry, setSelectedCountry] = useState(initialCountry);
    const [selectedState, setSelectedState] = useState(initialState);
    const [defaultFacility, setDefaultFacility] = useState('');
    const [loadingDefaultFacility, setLoadingDefaultFacility] = useState(false);

    React.useEffect(() => {
        const fetchFacility = async () => {
            if (!selectedCountry || !selectedState) return;

            try {
                const response = await dispatch(
                    getDefaultFacilityByLocation({
                        BusinessUnit: 'VIRS',
                        ProjectCountry: selectedCountry,
                        ProjectStateOrProvince: selectedState,
                        Label: 'Blue Label',
                    }),
                );

                setDefaultFacility(response['Result']);

                if (formikRef.current) {
                    formikRef.current.setFieldValue(
                        'defaultFacility',
                        response['Result'],
                    );
                    formikRef.current.setFieldValue(
                        'facility',
                        response['Result'],
                    );
                }
            } catch (err) {
                console.error('Error fetching facility:', err);
                setDefaultFacility('');
            }
        };

        fetchFacility();
    }, [selectedCountry, selectedState, dispatch]);

    const initialValues = {
        name: '',
        country: initialCountry,
        state: initialState,
        defaultFacility: defaultFacility,
        facility: initialFacility,
        unionLabel: 'Blue Label',
        customerNumber: '',
        projectNumber: '',
    };

    if (
        initialCountry !== '' &&
        initialState !== '' &&
        !loadingDefaultFacility
    ) {
        setSelectedCountry(initialCountry);
        setSelectedState(initialState);
        setLoadingDefaultFacility(true);
    }

    return (
        <Modal
            fitContent
            show={show}
        >
            <div className="p-4">
                <h1>New Project</h1>
                <div>
                    {regions.length > 0 && (
                        <Formik
                            innerRef={formikRef}
                            initialValues={initialValues}
                            validate={handleValidate}
                            onSubmit={handleSubmit}
                        >
                            {(formik) => {
                                const availableStates = [
                                    ...new Set(
                                        regions
                                            .filter(
                                                (r) =>
                                                    r.country ===
                                                    formik.values.country,
                                            )
                                            .map((r) => r.stateName),
                                    ),
                                ].map((s) => ({ name: s, value: s }));

                                return (
                                    <form onSubmit={formik.handleSubmit}>
                                        {Object.keys(formik.errors ?? {}).map(
                                            (k) =>
                                                formik.touched?.[k] && (
                                                    <QuickAlert
                                                        key={k}
                                                        kind="error"
                                                        msg={formik.errors?.[k]}
                                                    />
                                                ),
                                        )}
                                        <div className="mb-4 w-96">
                                            <div className="mb-4">
                                                <label
                                                    className="block mb-2 text-sm font-medium"
                                                    htmlFor="name"
                                                >
                                                    Name
                                                </label>
                                                <div className="mb-4 swegon-ui-text-input-wrapper">
                                                    <input
                                                        type="text"
                                                        name="name"
                                                        onChange={
                                                            formik.handleChange
                                                        }
                                                        value={
                                                            formik.values.name
                                                        }
                                                    />
                                                </div>
                                                <label
                                                    className="block mb-2 text-sm font-medium"
                                                    htmlFor="projectNumber"
                                                >
                                                    Project Number
                                                </label>
                                                <div className="mb-4 swegon-ui-text-input-wrapper">
                                                    <input
                                                        type="text"
                                                        name="projectNumber"
                                                        onChange={
                                                            formik.handleChange
                                                        }
                                                        value={
                                                            formik.values
                                                                .projectNumber
                                                        }
                                                        readOnly
                                                    />
                                                </div>
                                                <label
                                                    className="block mb-2 text-sm font-medium"
                                                    htmlFor="customerNumber"
                                                >
                                                    Customer Number
                                                </label>
                                                <div className="mb-4 swegon-ui-text-input-wrapper">
                                                    <input
                                                        type="text"
                                                        name="customerNumber"
                                                        onChange={
                                                            formik.handleChange
                                                        }
                                                        value={
                                                            formik.values
                                                                .customerNumber
                                                        }
                                                        readOnly
                                                    />
                                                </div>
                                                <label
                                                    className="block mb-2 text-sm font-medium"
                                                    htmlFor="unionLabel"
                                                >
                                                    Union Label
                                                </label>
                                                <div className="mb-4 swegon-ui-text-input-wrapper">
                                                    <input
                                                        type="text"
                                                        name="unionLabel"
                                                        onChange={
                                                            formik.handleChange
                                                        }
                                                        value={
                                                            formik.values
                                                                .unionLabel
                                                        }
                                                    />
                                                </div>
                                                <label
                                                    className="block mb-2 text-sm font-medium"
                                                    htmlFor="country"
                                                >
                                                    Country
                                                </label>
                                                <div className="mb-4">
                                                    <SelectField
                                                        value={
                                                            formik.values
                                                                .country
                                                        }
                                                        onChange={(option) => {
                                                            setSelectedState(
                                                                '',
                                                            );
                                                            setSelectedCountry(
                                                                option.value,
                                                            );
                                                            formik.setFieldValue(
                                                                'country',
                                                                option.value,
                                                            );
                                                            const nextStates = [
                                                                ...new Set(
                                                                    regions
                                                                        .filter(
                                                                            (
                                                                                r,
                                                                            ) =>
                                                                                r.country ===
                                                                                option.value,
                                                                        )
                                                                        .map(
                                                                            (
                                                                                r,
                                                                            ) =>
                                                                                r.stateName,
                                                                        ),
                                                                ),
                                                            ].map((s) => ({
                                                                name: s,
                                                                value: s,
                                                            }));

                                                            formik.setFieldValue(
                                                                'state',
                                                                nextStates[0]
                                                                    ?.value ||
                                                                    '',
                                                            );
                                                            setSelectedState(
                                                                nextStates[0]
                                                                    ?.value ||
                                                                    '',
                                                            );
                                                        }}
                                                        options={
                                                            uniqueCountries
                                                        }
                                                        disabled
                                                    />
                                                </div>
                                                <label
                                                    className="block mb-2 text-sm font-medium"
                                                    htmlFor="State"
                                                >
                                                    State
                                                </label>
                                                <div className="mb-4">
                                                    <SelectField
                                                        value={
                                                            formik.values.state
                                                        }
                                                        onChange={(option) => {
                                                            setSelectedState(
                                                                option.value,
                                                            );
                                                            formik.setFieldValue(
                                                                'state',
                                                                option.value,
                                                            );
                                                        }}
                                                        options={
                                                            availableStates
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex justify-end">
                                            <button
                                                className="swegon-like-button outlined mr-4 !px-4 amber"
                                                type="button"
                                                onClick={onClose}
                                            >
                                                Close
                                            </button>
                                            <button
                                                className="swegon-like-button contained !px-4"
                                                type="submit"
                                                disabled={formik.isSubmitting}
                                            >
                                                Submit
                                            </button>
                                        </div>
                                    </form>
                                );
                            }}
                        </Formik>
                    )}
                </div>
            </div>
        </Modal>
    );
};
